import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  Text,
  Badge,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Button,
  Collapse,
  Heading,
} from '@chakra-ui/react';
import { FaSearch, FaChevronDown, FaExclamationTriangle } from 'react-icons/fa';
import { useSearchParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/SEO';
import EpisodeCard from '../components/EpisodeCard';
import TagFilter from '../components/TagFilter';
import episodesData from '../data/episodes.json';
import { episodeTags } from '../data/episodeTags';
import lastUpdated from '../data/lastUpdated.json';
import tags from '../data/tags.json';
import ScrollIndicator from '../components/ScrollIndicator';
import ScrollReveal from '../components/ScrollReveal';
import FanFavourites from '../components/FanFavourites';

const formatDate = (dateString) => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      throw new Error('Invalid date');
    }
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Date unavailable';
  }
};

const parseDuration = (durationStr) => {
  try {
    if (!durationStr || typeof durationStr !== 'string') {
      throw new Error('Invalid duration string');
    }
    const parts = durationStr.split(':');
    if (parts.length === 2) {
      return parseInt(parts[0]) * 60 + parseInt(parts[1]);
    }
    return parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
  } catch (error) {
    console.error('Error parsing duration:', error);
    return 0;
  }
};

const Episodes = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedTags, setSelectedTags] = useState([]);
  const [expandedSeasons, setExpandedSeasons] = useState(new Set());
  const [error, setError] = useState(null);
  const { seasons = [] } = episodesData || {};

  // Validate data
  useEffect(() => {
    try {
      if (!Array.isArray(seasons)) {
        throw new Error('Invalid seasons data');
      }
      if (!episodeTags || typeof episodeTags !== 'object') {
        throw new Error('Invalid episode tags data');
      }
      if (!Array.isArray(tags)) {
        throw new Error('Invalid tags data');
      }
      setError(null);
    } catch (error) {
      console.error('Data validation error:', error);
      setError('There was an error loading the episodes data. Please try refreshing the page.');
    }
  }, [seasons]);

  // Sort seasons in reverse order (latest first)
  const sortedSeasons = useMemo(() => {
    try {
      return [...seasons].sort((a, b) => {
        const seasonA = typeof a.season === 'string' ? parseInt(a.season) : a.season;
        const seasonB = typeof b.season === 'string' ? parseInt(b.season) : b.season;
        if (isNaN(seasonA) || isNaN(seasonB)) {
          throw new Error('Invalid season number');
        }
        return seasonB - seasonA;
      });
    } catch (error) {
      console.error('Error sorting seasons:', error);
      return seasons;
    }
  }, [seasons]);

  // Initialize state based on URL params and latest season
  useEffect(() => {
    try {
      const query = searchParams.get('search');
      const tagId = searchParams.get('tag');
      const matchingSeasons = new Set();

      if (tagId) {
        // Handle tag filter
        const validTag = tags.find(t => t.id === tagId);
        if (validTag) {
          setSelectedTags([tagId]);
          setSearchQuery(validTag.name);
          // Find matching seasons
          sortedSeasons.forEach(season => {
            if (!season || !Array.isArray(season.episodes)) {
              throw new Error('Invalid season data');
            }
            const hasMatch = season.episodes.some(episode => {
              const episodeTagIds = episodeTags[episode.id] || [];
              return episodeTagIds.includes(tagId);
            });
            if (hasMatch) {
              matchingSeasons.add(season.season);
            }
          });
        } else {
          setSearchParams({}); // Clear invalid tag from URL
        }
      } else if (query) {
        // Handle search query
        setSearchQuery(query);
        const matchingTag = tags.find(t => t.name.toLowerCase() === query.toLowerCase());
        if (matchingTag) {
          setSelectedTags([matchingTag.id]);
          sortedSeasons.forEach(season => {
            if (!season || !Array.isArray(season.episodes)) {
              throw new Error('Invalid season data');
            }
            const hasMatch = season.episodes.some(episode => {
              const episodeTagIds = episodeTags[episode.id] || [];
              return episodeTagIds.includes(matchingTag.id);
            });
            if (hasMatch) {
              matchingSeasons.add(season.season);
            }
          });
        } else {
          // Search by title
          sortedSeasons.forEach(season => {
            if (!season || !Array.isArray(season.episodes)) {
              throw new Error('Invalid season data');
            }
            const hasMatch = season.episodes.some(episode =>
              episode.title.toLowerCase().includes(query.toLowerCase())
            );
            if (hasMatch) {
              matchingSeasons.add(season.season);
            }
          });
        }
      } else if (sortedSeasons.length > 0) {
        // No filters, show latest season
        matchingSeasons.add(sortedSeasons[0].season);
      }

      setExpandedSeasons(matchingSeasons);
      setError(null);
    } catch (error) {
      console.error('Error initializing state:', error);
      setError('There was an error loading your search results. Please try again.');
      setSearchParams({});
      setSelectedTags([]);
      setSearchQuery('');
      if (sortedSeasons.length > 0) {
        setExpandedSeasons(new Set([sortedSeasons[0].season]));
      }
    }
  }, [searchParams, sortedSeasons]);

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedTags([]);
    setSearchParams({});
  };

  const handleTagSelect = (tagId) => {
    // Toggle: deselect if already active
    if (selectedTags.includes(tagId)) {
      handleClearFilters();
      return;
    }
    setSelectedTags([tagId]);
    const tagName = tags.find(t => t.id === tagId)?.name || '';
    setSearchQuery(tagName);
    setSearchParams({ tag: tagId, search: tagName });
    
    // Find and expand seasons containing episodes with this tag
    const matchingSeasons = new Set();
    sortedSeasons.forEach(season => {
      if (!season || !Array.isArray(season.episodes)) {
        throw new Error('Invalid season data');
      }
      const hasMatch = season.episodes.some(episode => {
        const episodeTagIds = episodeTags[episode.id] || [];
        return episodeTagIds.includes(tagId);
      });
      if (hasMatch) {
        matchingSeasons.add(season.season);
      }
    });
    setExpandedSeasons(matchingSeasons);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Check if the search query matches any tag name
    const matchingTag = tags.find(t => 
      t.name.toLowerCase() === value.toLowerCase()
    );
    
    if (matchingTag) {
      handleTagSelect(matchingTag.id);
    } else {
      setSelectedTags([]);
      if (value) {
        setSearchParams({ search: value });
        // Find and expand seasons containing matching episodes
        const matchingSeasons = new Set();
        sortedSeasons.forEach(season => {
          if (!season || !Array.isArray(season.episodes)) {
            throw new Error('Invalid season data');
          }
          const hasMatch = season.episodes.some(episode =>
            episode.title.toLowerCase().includes(value.toLowerCase())
          );
          if (hasMatch) {
            matchingSeasons.add(season.season);
          }
        });
        setExpandedSeasons(matchingSeasons);
      } else {
        setSearchParams({});
        // Reset to latest season
        if (sortedSeasons.length > 0) {
          setExpandedSeasons(new Set([sortedSeasons[0].season]));
        }
      }
    }
  };

  const toggleSeason = (seasonNumber) => {
    setExpandedSeasons(prev => {
      const next = new Set(prev);
      if (next.has(seasonNumber)) {
        next.delete(seasonNumber);
      } else {
        next.add(seasonNumber);
      }
      return next;
    });
  };

  const filteredSeasons = useMemo(() => {
    return sortedSeasons.map(season => ({
      ...season,
      episodes: season.episodes.filter(episode => {
        const episodeTagIds = episodeTags[episode.id] || [];
        const matchesTags = selectedTags.length === 0 || 
          selectedTags.every(tag => episodeTagIds.includes(tag));

        // If we have selected tags, only filter by tags
        if (selectedTags.length > 0) {
          return matchesTags;
        }
        
        // Otherwise, filter by search query
        return episode.title.toLowerCase().includes(searchQuery.toLowerCase());
      })
    })).filter(season => season.episodes.length > 0);
  }, [sortedSeasons, searchQuery, selectedTags]);

  return (
    <Box minH="100vh" pt={{ base: 20, md: 20 }} pb={{ base: 12, md: 20 }} id="episodes-container">
      <SEO
        title="Episodes"
        description="Explore our collection of Indian history and mythology episodes. Journey through ancient civilizations, cultural heritage, mythological tales, and fascinating stories from India's past."
        type="website"
        canonicalUrl="/episodes"
        keywords="History Episodes, Indian Mythology Episodes, Ancient India Stories, Cultural Heritage Stories, Historical Podcast Episodes, Mythological Tales India, Epic Stories India, Hindu Mythology Stories"
        image="/images/episodes-banner.jpg"
        publishedTime={lastUpdated.timestamp}
        modifiedTime={lastUpdated.timestamp}
        language="en"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'PodcastSeries',
          name: 'Namaskar India',
          url: 'https://namaskarindia.live',
          description: 'A captivating podcast exploring Indian history and mythology. Journey through ancient tales, cultural heritage, and fascinating stories from India\'s past.',
          inLanguage: 'en',
          image: 'https://namaskarindia.live/images/logo.png',
          author: { '@type': 'Person', name: 'Aradhana Duppala' },
          publisher: {
            '@type': 'Organization',
            name: 'Namaskar India',
            logo: { '@type': 'ImageObject', url: 'https://namaskarindia.live/logo.png' }
          },
          episode: seasons.flatMap(season => season.episodes.map(ep => ({
            '@type': 'PodcastEpisode',
            name: ep.title,
            description: ep.description,
            datePublished: ep.pubDate,
            timeRequired: `PT${Math.floor(ep.duration / 60)}M${ep.duration % 60}S`,
            url: ep.audioUrl,
            partOfSeries: { '@type': 'PodcastSeries', name: 'Namaskar India' },
          })))
        })}</script>
      </Helmet>

      <Container maxW="container.xl">
        <VStack spacing={{ base: 8, md: 10 }} align="stretch">
          {/* Header */}
          <VStack spacing={4} maxW="3xl" mx="auto" textAlign="center">
            <Heading
              as="h1"
              fontSize={{ base: "3xl", sm: "4xl", md: "6xl", lg: "7xl" }}
              lineHeight="1.2"
              bgGradient="linear(to-r, brand.400, terracotta.400)"
              bgClip="text"
              mb={{ base: 2, md: 4 }}
              style={{ filter: 'drop-shadow(0 0 20px rgba(255,152,0,0.25))' }}
            >
              Explore Our Episodes
            </Heading>

            <Text 
              fontSize={{ base: "lg", md: "xl" }} 
              color="whiteAlpha.900" 
              maxW="2xl"
              px={{ base: 4, md: 0 }}
            >
              Stories from Indian history and mythology, carefully crafted to bring the past to life.
            </Text>
          </VStack>

          {/* Fan Favourites carousel — breaks out of container width */}
          <Box mx={{ base: -4, md: -8, lg: -16 }}>
            <FanFavourites />
          </Box>

          {/* Error state */}
          {error && (
            <VStack
              layerStyle="glassCard"
              borderColor="rgba(229,101,53,0.4)"
              spacing={3}
              p={8}
              textAlign="center"
              position="relative"
              overflow="hidden"
            >
              <Box position="absolute" top={0} left={0} right={0} h="2px" bgGradient="linear(to-r, transparent, terracotta.400, transparent)" />
              <Icon as={FaExclamationTriangle} boxSize={6} color="terracotta.300" />
              <Text fontSize="lg" fontWeight="bold" color="whiteAlpha.900">
                Oops! Something went wrong
              </Text>
              <Text maxWidth="sm" color="whiteAlpha.800">
                {error}
              </Text>
            </VStack>
          )}

          {/* Search and Filters */}
          {!error && (
            <>
              {/* Search Bar */}
              <Box maxW="2xl" mx="auto" w="full" px={4}>
                <Box position="relative" overflow="hidden" borderRadius="lg">
                  <Box position="absolute" top={0} left={0} right={0} h="2px" bgGradient="linear(to-r, transparent, brand.500, transparent)" zIndex={1} />
                  <InputGroup size="lg">
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FaSearch} color="whiteAlpha.400" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search episodes..."
                      aria-label="Search episodes"
                      value={searchQuery}
                      onChange={handleSearchChange}
                      bg="rgba(255,150,50,0.05)"
                      backdropFilter="blur(12px)"
                      border="1px solid rgba(255,150,50,0.12)"
                      _hover={{ borderColor: 'rgba(255,152,0,0.3)' }}
                      _focus={{
                        borderColor: 'rgba(255,152,0,0.5)',
                        boxShadow: '0 0 0 1px rgba(255,152,0,0.2)',
                        bg: 'rgba(255,150,50,0.09)'
                      }}
                      color="whiteAlpha.900"
                      _placeholder={{ color: 'whiteAlpha.400' }}
                    />
                  </InputGroup>
                </Box>
                {(searchQuery || selectedTags.length > 0) && (
                  <Box mt={2}>
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="whiteAlpha"
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedTags([]);
                        setSearchParams({});
                      }}
                    >
                      Clear filters
                    </Button>
                    {selectedTags.length > 0 && (
                      <Text fontSize="sm" color="whiteAlpha.600" mt={2}>
                        Showing episodes with tag: {tags.find(t => t.id === selectedTags[0])?.name}
                      </Text>
                    )}
                  </Box>
                )}
              </Box>

              {/* Tag Filter */}
              <Box maxW="2xl" mx="auto" w="full" px={4}>
                <TagFilter
                  selectedTags={selectedTags}
                  onTagSelect={handleTagSelect}
                  onClear={handleClearFilters}
                />
              </Box>

              {/* Last Updated */}
              <Text fontSize="xs" color="whiteAlpha.400" textAlign="center" letterSpacing="wide">
                Updated {formatDate(lastUpdated.timestamp)}
              </Text>

              {/* Episodes List */}
              {filteredSeasons.length === 0 ? (
                <VStack layerStyle="glassTile" spacing={4} py={10} px={6}>
                  {/* Static waveform motif — an empty state shouldn't animate */}
                  <HStack spacing="4px" align="flex-end" h="28px">
                    {[12, 22, 16, 26, 10].map((h, i) => (
                      <Box
                        key={i}
                        w="4px"
                        h={`${h}px`}
                        borderRadius="2px"
                        bgGradient="linear(to-t, terracotta.400, brand.500)"
                        opacity={0.7}
                      />
                    ))}
                  </HStack>
                  <Text fontSize="lg" color="whiteAlpha.900">
                    No episodes match your search.
                  </Text>
                  <Button
                    variant="outline"
                    colorScheme="brand"
                    size="sm"
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedTags([]);
                      setSearchParams({});
                    }}
                  >
                    Clear search and filters
                  </Button>
                </VStack>
              ) : (
                <Box>
                  {filteredSeasons.map((season, seasonIdx) => (
                    <ScrollReveal key={season.season} delay={Math.min(seasonIdx * 0.06, 0.25)}>
                    <Box
                      border="1px solid"
                      borderColor={expandedSeasons.has(season.season) ? 'rgba(255,152,0,0.4)' : 'rgba(255,150,50,0.18)'}
                      borderRadius="xl"
                      mb={4}
                      background="rgba(40,24,8,0.95)"
                      backdropFilter="blur(12px)"
                      boxShadow={expandedSeasons.has(season.season) ? '0 8px 30px rgba(255,152,0,0.12)' : '0 4px 24px rgba(0,0,0,0.5)'}
                      _hover={{
                        borderColor: 'rgba(255,152,0,0.45)',
                        boxShadow: '0 8px 30px rgba(255,152,0,0.12)',
                      }}
                      transition="all 0.25s"
                    >
                      <Button
                        w="100%"
                        h="auto"
                        py={6}
                        px={6}
                        onClick={() => toggleSeason(season.season)}
                        bg="transparent"
                        _hover={{ bg: 'rgba(255,150,50,0.06)' }}
                        _active={{ bg: 'rgba(255,150,50,0.1)' }}
                        display="flex"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        borderRadius="lg"
                        borderBottomRadius={expandedSeasons.has(season.season) ? 0 : 'xl'}
                        borderBottom={expandedSeasons.has(season.season) ? '1px solid' : 'none'}
                        borderColor="rgba(255,150,50,0.2)"
                        transition="all 0.2s"
                      >
                        <VStack align="stretch" spacing={3} flex={1} maxW="calc(100% - 40px)">
                          <HStack spacing={2} width="100%" flexWrap="wrap" align="center">
                            <Badge
                              colorScheme="orange"
                              fontSize={{ base: "sm", md: "md" }}
                              px={3}
                              py={1}
                              borderRadius="full"
                              textTransform="none"
                              flexShrink={0}
                            >
                              Season {season.season}
                            </Badge>
                            <Text
                              fontSize={{ base: "lg", md: "2xl" }}
                              fontWeight="bold"
                              color="whiteAlpha.900"
                              flex={1}
                              isTruncated
                              textAlign="left"
                            >
                              {season.name}
                            </Text>
                            <Badge
                              colorScheme="gray"
                              fontSize={{ base: "xs", md: "sm" }}
                              px={2}
                              py={1}
                              borderRadius="full"
                              textTransform="none"
                              flexShrink={0}
                            >
                              {season.episodes.length} {season.episodes.length === 1 ? 'Episode' : 'Episodes'}
                            </Badge>
                          </HStack>
                          <Text
                            fontSize={{ base: "sm", md: "md" }}
                            color="brand.300"
                            fontStyle="italic"
                            textAlign="left"
                            wordBreak="break-word"
                            whiteSpace="pre-wrap"
                          >
                            {season.description}
                          </Text>
                        </VStack>
                        <Icon
                          as={FaChevronDown}
                          boxSize={{ base: 4, md: 6 }}
                          ml={4}
                          mt={2}
                          transform={expandedSeasons.has(season.season) ? 'rotate(180deg)' : 'none'}
                          transition="transform 0.2s"
                        />
                      </Button>

                      <Collapse in={expandedSeasons.has(season.season)} unmountOnExit>
                        <Box py={6} px={{ base: 4, md: 6 }}>
                          <VStack spacing={4} align="stretch">
                            {season.episodes.map((episode, idx) => (
                              <Box
                                key={episode.id || episode.enclosure?.url}
                                sx={{
                                  animation: 'card-slide-in 0.4s ease both',
                                  animationDelay: `${Math.min(idx * 0.05, 0.6)}s`,
                                }}
                              >
                                <EpisodeCard
                                  episode={episode}
                                  duration={parseDuration(episode.duration)}
                                  isHighlighted={searchQuery && episode.title.toLowerCase().includes(searchQuery.toLowerCase())}
                                />
                              </Box>
                            ))}
                          </VStack>
                        </Box>
                      </Collapse>
                    </Box>
                    </ScrollReveal>
                  ))}
                </Box>
              )}
            </>
          )}
        </VStack>
      </Container>
      <ScrollIndicator containerId="episodes-container" />
    </Box>
  );
};

export default Episodes;